let activeGenerationRunId: string | null = null;
let activeGenerationProjectId: string | null = null;

export function setActiveGenerationRun(runId: string | null, projectId: string | null = null): void {
  activeGenerationRunId = runId;
  activeGenerationProjectId = projectId;
}

export function getActiveGenerationRunId(): string | null {
  return activeGenerationRunId;
}

export function getActiveGenerationProjectId(): string | null {
  return activeGenerationProjectId;
}

export function resolveGenerationRunId(existing?: string | null): string {
  if (existing) return existing;
  if (activeGenerationRunId) return activeGenerationRunId;
  return crypto.randomUUID();
}

export type ReservationErrorKind =
  | 'rate_limited'
  | 'insufficient_credits'
  | 'missing_project'
  | 'no_entitlement'
  | 'invalid_state'
  | 'orphaned_run_cleared'
  | 'unknown';

export interface ActiveRunMetadata {
  runId: string;
  projectId: string;
  operation: string;
  status: string;
  leaseExpiresAt: string;
}

export interface ParsedReservationError {
  kind: ReservationErrorKind;
  message: string;
  reason?: string;
  httpStatus?: number;
  retryAfterSeconds?: number;
  activeRunId?: string;
  activeRun?: ActiveRunMetadata;
  sourceProjectDeleted?: boolean;
  requestId?: string;
}

export function parseReservationError(status: number, body: Record<string, unknown>): ParsedReservationError {
  const error = String(body.error || '');
  const reason = String(body.reason || body.code || '');
  const message = String(body.message || error || 'Reservation failed');
  const base = { reason, httpStatus: status };
  const retryAfterSeconds =
    typeof body.retryAfterSeconds === 'number'
      ? body.retryAfterSeconds
      : typeof body.retryAfter === 'number'
        ? body.retryAfter
        : undefined;

  const activeRunRaw = body.activeRun;
  const activeRun: ActiveRunMetadata | undefined =
    activeRunRaw && typeof activeRunRaw === 'object'
      ? {
          runId: String((activeRunRaw as ActiveRunMetadata).runId || body.activeRunId || ''),
          projectId: String((activeRunRaw as ActiveRunMetadata).projectId || ''),
          operation: String((activeRunRaw as ActiveRunMetadata).operation || ''),
          status: String((activeRunRaw as ActiveRunMetadata).status || ''),
          leaseExpiresAt: String((activeRunRaw as ActiveRunMetadata).leaseExpiresAt || ''),
        }
      : typeof body.activeRunId === 'string'
        ? {
            runId: body.activeRunId,
            projectId: '',
            operation: '',
            status: '',
            leaseExpiresAt: '',
          }
        : undefined;

  if (status === 429 || error === 'RATE_LIMIT_EXCEEDED' || reason === 'ACTIVE_RUN_EXISTS') {
    return {
      ...base,
      kind: 'rate_limited',
      message: 'Another generation is already running.',
      retryAfterSeconds: retryAfterSeconds ?? 60,
      activeRunId: activeRun?.runId,
      activeRun,
      requestId: typeof body.requestId === 'string' ? body.requestId : undefined,
    };
  }
  if (status === 502 || status === 503 || status === 504) {
    return {
      ...base,
      kind: 'unknown',
      reason: 'gateway_error',
      message: 'Chronovela could not verify your manuscript credit. No credit was used. Please try again.',
    };
  }
  if (reason === 'insufficient_tokens' || reason === 'INSUFFICIENT_REWRITE_CREDITS' || reason === 'insufficient_rewrite_credits') {
    return { ...base, kind: 'insufficient_credits', message: 'Insufficient credits for this action.' };
  }
  if (reason === 'missing_project' || reason === 'MISSING_PROJECT') {
    return { ...base, kind: 'missing_project', message: 'Project setup is still saving. Please retry in a moment.' };
  }
  if (reason === 'no_manuscript_entitlement' || reason === 'NO_MANUSCRIPT_ENTITLEMENT') {
    return { ...base, kind: 'no_entitlement', message: 'A manuscript credit must be applied before generating this chapter.' };
  }
  if (reason === 'free_preview_already_used' || reason === 'FREE_PREVIEW_ALREADY_USED') {
    return {
      ...base,
      kind: 'no_entitlement',
      message: 'A manuscript credit is required to continue beyond the free preview.',
    };
  }
  if (reason === 'chapter_limit_exceeded' || reason === 'CHAPTER_LIMIT_EXCEEDED') {
    return {
      ...base,
      kind: 'no_entitlement',
      message: 'Your current manuscript tier does not cover this chapter. Upgrade to continue.',
    };
  }
  if (reason === 'invalid_state' || reason === 'INVALID_STATE') {
    return { ...base, kind: 'invalid_state', message: 'This generation attempt was already used. Retry or resume the active run.' };
  }
  if (reason === 'invalid_operation' || reason === 'INVALID_OPERATION') {
    return {
      ...base,
      kind: 'unknown',
      reason: 'invalid_operation',
      message: 'Chronovela could not start this generation because the reservation request was invalid. No credit was used. Please try again.',
    };
  }
  if (reason === 'server_error' || status >= 500) {
    return {
      ...base,
      kind: 'unknown',
      reason: 'server_error',
      message: 'Chronovela could not verify your manuscript credit. No credit was used. Please try again.',
    };
  }
  return { ...base, kind: 'unknown', message };
}

export async function reconcileStaleActiveRun(apiBase: string, idToken: string): Promise<{ cleared: boolean; action?: string }> {
  const response = await fetch(`${apiBase}/api/generation/reconcile-active-run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) return { cleared: false };
  const data = await response.json().catch(() => ({}));
  return { cleared: !!data.cleared, action: data.action };
}
